import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Flex } from '@electricjs/arc';

import { FirstStepTile } from '@/components/Storefront/components/International/FirstStepTile';
import { SecondStepTile } from '@/components/Storefront/components/International/SecondStepTile';
import { InternationalRequestSuccessModal } from '../../../../pages/Storefront/InternationalRequestSuccessModal';
import { useCreateHardwareQuoteMutation } from '@/redux/slices/orderApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { CreateQuoteArgs } from '@/types/orders';
import { useStorefront } from '@/components/Storefront/StorefrontProvider';

const OtherCountryFlow = () => {
  const { t } = useTranslation('pages');

  const {
    storefrontCheckoutForm,
    otherCountryFlowFirstStepForm,
    otherCountryFlowSecondStepForm,
    otherCountryFlowStep,
    resetStorefront,
  } = useStorefront();

  const navigate = useNavigate();

  const organizationId = useGetOrganizationId();
  const { showErrorToast, showSuccessToast, openModal } = useGlobalUI();
  const { internationalHardwareQuotingV2 } = useFlags();

  const [createQuote, { isLoading: isSubmittingQuote }] =
    useCreateHardwareQuoteMutation();

  const handleSecondStepSubmit = () => {
    if (
      !storefrontCheckoutForm ||
      !otherCountryFlowFirstStepForm ||
      !otherCountryFlowSecondStepForm
    ) {
      throw new Error();
    }

    const requestArgs: CreateQuoteArgs = {
      organizationId,
      employeeId: storefrontCheckoutForm.getValues('employee.value') ?? '',
      recipientNationalId:
        otherCountryFlowFirstStepForm.getValues('recipientId'),
      shippingAddress: otherCountryFlowFirstStepForm.getValues('address'),
      phoneNumber: otherCountryFlowFirstStepForm.getValues('phone.phoneNumber'),
      email: otherCountryFlowFirstStepForm.getValues('email'),
      productId: otherCountryFlowSecondStepForm.getValues(
        'selectedHardware.id'
      ),
      extendedWarrantyId: undefined, // nothing for now, until warranties are added to the catalog
      targetDeliveryDate:
        otherCountryFlowSecondStepForm.getValues('targetDeliveryDate') ??
        new Date(),
    };

    createQuote(requestArgs)
      .unwrap()
      .then(result => {
        if ('isManualFulfillment' in result) {
          showSuccessToast({
            id: 'international-procurement-success-toast',
            message: t(
              'internationalOrderRequest.manualFulfillmentQuoteSubmittedSuccess'
            ),
          });
          resetStorefront();
          return;
        }
        if (internationalHardwareQuotingV2) {
          showSuccessToast({
            id: 'international-procurement-success-toast',
            message: t('internationalOrderRequest.quoteSubmittedSuccess'),
          });

          navigate(`/quotes/${result.friendlyId}`, {
            state: { origin: 'storefront' },
          });
        } else {
          // Legacy flow - show modal and redirect to storefront
          openModal(<InternationalRequestSuccessModal />);
        }

        resetStorefront();
      })
      .catch(() => {
        showErrorToast({
          id: 'international-procurement-error-toast',
          message: t('internationalOrderRequest.errors.submitFailed'),
        });
      });
  };

  return (
    <Flex vertical width="100%">
      {otherCountryFlowStep === 1 && <FirstStepTile />}

      {otherCountryFlowStep === 2 && (
        <SecondStepTile
          isSubmitting={isSubmittingQuote}
          onRequest={handleSecondStepSubmit}
        />
      )}
    </Flex>
  );
};

export default OtherCountryFlow;
